import { getGroupStats, orUndefined } from '@lms/api-client/backend';
import { ChartCard, DonutChart, KpiTile, useSeries } from '@lms/charts';
import { LoaderCircle } from 'lucide-react';
import useSWR from 'swr';
import { useLocale, useTranslations } from 'use-intl';

const countOf = (r: { count?: number | null }) => r.count ?? 0;

export function GroupStats({ groupId }: { groupId: number }) {
  const locale = useLocale();
  const t = useTranslations('stats');
  const { data: stats } = useSWR(['group-stats', groupId, locale], () =>
    orUndefined(getGroupStats(groupId, locale)),
  );
  const year = new Date().getFullYear();
  const other = t('other');

  const perCourse = stats?.completedPerCourse ?? [];
  const perCurriculum = stats?.completedPerCurriculum ?? [];
  const courses = useSeries(perCourse, (r) => r.courseName, countOf, {
    max: 6,
    otherLabel: other,
  });
  const curricula = useSeries(perCurriculum, (r) => r.curriculumName, countOf, {
    max: 6,
    otherLabel: other,
  });

  if (stats === undefined) {
    return (
      <div className="mb-12 flex h-32 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mb-12 grid w-full grid-cols-1 gap-4 md:grid-cols-2">
      <KpiTile
        label={t('sessions')}
        description={t('during_year', { year })}
        value={stats.students}
        locale={locale}
      />
      <KpiTile
        label={t('completed_courses')}
        description={t('during_year', { year })}
        value={perCurriculum.reduce((sum, e) => sum + countOf(e), 0)}
        locale={locale}
      />
      <ChartCard
        title={t('completed_courses')}
        description={t('per_course_year', { year })}
      >
        <DonutChart
          data={perCourse}
          name={(r) => r.courseName}
          value={countOf}
          seriesSet={courses}
          size={148}
          locale={locale}
          emptyLabel={t('no_data')}
        />
      </ChartCard>
      <ChartCard
        title={t('completed_courses')}
        description={t('per_curriculum_year', { year })}
      >
        <DonutChart
          data={perCurriculum}
          name={(r) => r.curriculumName}
          value={countOf}
          seriesSet={curricula}
          size={148}
          locale={locale}
          emptyLabel={t('no_data')}
        />
      </ChartCard>
    </div>
  );
}
